import Nav from "@/components/Nav";
import MarketingFooter from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { Check, X, Sparkles, ShieldCheck, AlertTriangle, ScrollText, TrendingUp, MessageSquare, Moon, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { usePageMeta } from "@/lib/pageMeta";

// Core comparison rows — fully genericised, no specific competitor names
const ROWS = [
  { label: "AI compatibility with explainable reasoning", anubandh: "yes", global_ai: "yes", indian_ai_app: "score-only", listing_portal: "no", assisted_portal: "no", swipe_app: "score-only" },
  { label: "Kundali × AI Fusion Report (bundled)", anubandh: "yes", global_ai: "no", indian_ai_app: "paid add-on", listing_portal: "no", assisted_portal: "paid add-on", swipe_app: "no" },
  { label: "Deal-Breaker Detector", anubandh: "yes", global_ai: "no", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Elder Letter (for parents)", anubandh: "yes", global_ai: "no", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Growth Trajectory Score", anubandh: "yes", global_ai: "no", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "First 3 Questions (personalised)", anubandh: "yes", global_ai: "generic", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Two of You — Chemistry sub-scores", anubandh: "yes", global_ai: "yes", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Score Confidence Meter", anubandh: "yes", global_ai: "no", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Family-shareable PDF report", anubandh: "yes", global_ai: "no", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Family Circle (multi-member access)", anubandh: "yes", global_ai: "no", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Weekly curated matches (quality > quantity)", anubandh: "3/week", global_ai: "daily", indian_ai_app: "high volume", listing_portal: "browse", assisted_portal: "browse", swipe_app: "swipe" },
  { label: "ID + selfie KYC verification", anubandh: "yes", global_ai: "partial", indian_ai_app: "yes", listing_portal: "partial", assisted_portal: "yes", swipe_app: "yes" },
  { label: "Explainable dimension radar", anubandh: "yes", global_ai: "yes", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Marriage Readiness Score", anubandh: "yes", global_ai: "no", indian_ai_app: "no", listing_portal: "no", assisted_portal: "no", swipe_app: "no" },
  { label: "Starting price / month", anubandh: "₹1,999", global_ai: "₹2,500", indian_ai_app: "₹733", listing_portal: "₹1,513", assisted_portal: "₹1,766", swipe_app: "₹599" },
];

const UNIQUE_FEATURES = [
  {
    icon: AlertTriangle,
    title: "Deal-Breaker Detector",
    body: "AI + heuristic engine that surfaces non-negotiable conflicts — diet, joint-vs-nuclear, inter-faith, city relocation — before they become the fight in year three.",
    who: "Not offered by any global psychology platform, Indian matrimony portal, or dating app we surveyed.",
  },
  {
    icon: ScrollText,
    title: "Elder Letter",
    body: "A respectful 3-4 sentence letter addressed TO your parents, in formal English with a Sanskrit closer. Written for the elders — not to route around them.",
    who: "The only platform globally that treats family as a first-class reader of the report.",
  },
  {
    icon: TrendingUp,
    title: "Growth Trajectory Score",
    body: "Predicts whether this pair grows together or apart over 5+ years — based on personal-growth, emotional-intelligence, and communication signals.",
    who: "Global psychology platforms hint at long-term compatibility. We quantify it.",
  },
  {
    icon: MessageSquare,
    title: "First 3 Questions",
    body: "Three specific, personalised questions written for the exact profile pair — not generic icebreakers. Designed to be asked on your first meaningful call.",
    who: "Other platforms rely on templated prompts. Ours are custom-authored per pair.",
  },
  {
    icon: Heart,
    title: "Two of You — Chemistry",
    body: "Four dedicated sub-scores: Intellectual Chemistry, Emotional Resonance, Life-Rhythm Fit, Values Alignment — beyond the single overall percentage.",
    who: "Adapted from Western compatibility science, redesigned for Indian families and made fully explainable.",
  },
  {
    icon: Moon,
    title: "Kundali × AI Fusion",
    body: "Vedic Ashtakoot Guna Milan (36 gunas) fused with modern AI compatibility across 8 dimensions — one score, one report. Not a paid add-on.",
    who: "Indian portals charge extra for horoscope matching. Global platforms ignore astrology entirely.",
  },
];

function Cell({ v, primary }) {
  if (v === "yes") return <div className={`flex items-center gap-1 ${primary ? "text-amber-700" : "text-emerald-700"}`}><Check size={16} /><span className="text-xs">Included</span></div>;
  if (v === "no") return <div className="flex items-center gap-1 text-stone-400"><X size={16} /><span className="text-xs">No</span></div>;
  return <div className={`text-xs ${primary ? "text-[#1E1B4B] font-medium" : "text-stone-600"}`}>{v}</div>;
}

const COLS = [
  { key: "anubandh", label: "Anubandh.ai", tag: "India · Fusion" },
  { key: "global_ai", label: "Global AI Platform", tag: "Psychology-based" },
  { key: "indian_ai_app", label: "AI Matrimony App", tag: "India · AI-based" },
  { key: "listing_portal", label: "Legacy Listing Portal", tag: "India · Browse" },
  { key: "assisted_portal", label: "Assisted Portal", tag: "India · Community" },
  { key: "swipe_app", label: "Swipe App", tag: "India · Dating" },
];

export default function Compare() {
  usePageMeta(
    "AI Engine vs Listing Sites",
    "How Anubandh.ai — an AI Marriage Intelligence Engine — differs from listing sites and assisted portals across 15 dimensions."
  );
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav variant="marketing" />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-8">
        <div className="overline">How Anubandh Compares</div>
        <h1 className="font-serif-display text-4xl lg:text-6xl text-[#1E1B4B] mt-4 leading-[1.05]">
          Everyone shows you profiles.<br />
          <span className="italic text-amber-700">We show you a decision.</span>
        </h1>
        <p className="mt-6 text-stone-600 max-w-2xl">
          We benchmarked ourselves honestly against the world&apos;s top matchmaking categories —
          from global psychology-based compatibility platforms to India&apos;s biggest matrimony
          portals. Here&apos;s the full grid, and the six things nobody else offers.
        </p>
      </section>

      {/* SIX UNIQUE FEATURES — the hero-grade selling story */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-16">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Unique to Anubandh</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E1B4B] mt-2 leading-tight">
              Six features you&apos;ll find <span className="italic text-amber-700">nowhere else.</span>
            </h2>
          </div>
          <p className="text-sm text-stone-500 max-w-md">
            Every one of these ships inside the ₹1,999 <strong>Clarity</strong> tier — not as add-ons, not as upsells.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {UNIQUE_FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.05 }}
              data-testid={`unique-feature-${i}`}
              className="rounded-2xl border border-stone-200 bg-white p-7 hover:border-amber-300 hover:shadow-[0_20px_50px_-25px_rgba(30,27,75,0.25)] transition-all"
            >
              <div className="w-11 h-11 rounded-full bg-amber-50 border border-amber-200 grid place-items-center">
                <f.icon className="text-amber-700" size={20} />
              </div>
              <h3 className="font-serif-display text-xl text-[#1E1B4B] mt-5 leading-snug">{f.title}</h3>
              <p className="mt-3 text-sm text-stone-600 leading-relaxed">{f.body}</p>
              <div className="mt-5 pt-4 border-t border-stone-100 text-xs italic text-amber-800">
                {f.who}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* THE HONEST GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="overline">The Honest Grid</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E1B4B] mt-2 leading-tight">Every feature. Every competitor. No spin.</h2>
          </div>
          <p className="text-sm text-stone-500 max-w-md">
            Where we lose, we admit it — swipe apps are cheaper, listing portals are older. Where we win, we&apos;ve earned it.
          </p>
        </div>

        <div className="border border-stone-200 rounded-2xl bg-white overflow-hidden overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-7 gap-0 border-b border-stone-200 bg-[#FEFBF3]">
              <div className="p-5 text-xs uppercase tracking-widest text-stone-500">Feature</div>
              {COLS.map((c) => (
                <div key={c.key} className={`p-5 text-center ${c.key === "anubandh" ? "bg-[#1E1B4B] text-white" : ""}`}>
                  <div className={`font-serif-display text-base ${c.key === "anubandh" ? "font-medium" : ""}`}>{c.label}</div>
                  {c.tag && <div className={`text-[9px] uppercase tracking-widest mt-1 ${c.key === "anubandh" ? "text-amber-300" : "text-stone-500"}`}>{c.tag}</div>}
                </div>
              ))}
            </div>
            {ROWS.map((r, idx) => (
              <div key={r.label} className={`grid grid-cols-7 gap-0 items-center ${idx % 2 === 0 ? "bg-white" : "bg-[#FEFBF3]/40"} border-t border-stone-100`}>
                <div className="p-4 text-sm text-stone-700">{r.label}</div>
                {COLS.map((c) => (
                  <div key={c.key} className={`p-4 flex justify-center ${c.key === "anubandh" ? "bg-amber-50/60" : ""}`}>
                    <Cell v={r[c.key]} primary={c.key === "anubandh"} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Method note */}
        <p className="text-xs text-stone-500 mt-4 italic">
          Method: Data compiled from publicly available product pages of platforms in each category (Feb 2026). Feature parity based on disclosed capability descriptions. We recheck this grid quarterly.
        </p>
      </section>

      {/* Why we chose this shape */}
      <section className="py-20 border-t border-stone-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Sparkles, t: "Why we bundle Kundali", d: "Most matrimony apps sell Kundali reports as a paid add-on (₹800+). We include a Kundali × AI Fusion inside every Clarity report — because a decision shouldn't come in fragments." },
            { icon: ShieldCheck, t: "Why 3 curated matches beat 50", d: "50 daily matches sound generous. They aren't. They're overwhelm dressed as choice. We deliver a small, thoughtful set per week — each with a full report." },
            { icon: Heart, t: "Why we treat family as a user", d: "No competitor lets your parents read your compatibility reports safely. Our Family Circle gives permissioned access to up to 4 elders — quietly organized, culturally understood." },
          ].map((c) => (
            <motion.div key={c.t}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="border border-stone-200 rounded-2xl p-7 bg-[#F9F8F6]">
              <c.icon size={22} className="text-amber-700" />
              <h3 className="font-serif-display text-xl text-[#1E1B4B] mt-5">{c.t}</h3>
              <p className="mt-3 text-sm text-stone-600 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-14">
          <Link to="/signup" data-testid="compare-cta-begin" className="btn-pill inline-flex text-white px-8 py-4 glow-gold" style={{ background: "#1E1B4B" }}>
            Try Anubandh.ai Free — no card required
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
